import { useMemo, useState } from "react";
import { useFetch } from "../lib/useFetch";

const DAYS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];

export const Traeningstider = () => {
  const { data: training, loading } = useFetch("/training");

  const teams = useMemo(
    () => ["Alle", ...Array.from(new Set(training.map((t) => t.team)))],
    [training]
  );
  const [filter, setFilter] = useState("Alle");

  const rows = useMemo(() => {
    const src = filter === "Alle" ? training : training.filter((t) => t.team === filter);
    return [...src].sort((a, b) => {
      const da = DAYS.indexOf(a.day);
      const db = DAYS.indexOf(b.day);
      if (da !== db) return da - db;
      return (a.time || "").localeCompare(b.time || "");
    });
  }, [filter, training]);

  return (
    <section
      id="traening"
      data-testid="traeningstider-section"
      className="relative border-t border-neutral-900 bg-neutral-950 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="section-eyebrow">02 · Ugeplan</span>
            <h2 className="mt-5 font-heading text-5xl uppercase leading-none tracking-tight text-white sm:text-6xl lg:text-7xl">
              Træningstider
            </h2>
            <p className="mt-4 max-w-2xl font-body text-neutral-400">
              Alle hold træner fast uge efter uge. Ønsker du at prøvetræne? Kom
              bare forbi — eller kontakt træneren på forhånd.
            </p>
          </div>

          <div
            className="flex flex-wrap gap-2"
            data-testid="traening-filter-group"
          >
            {teams.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`traening-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setFilter(t)}
                className={`border px-3 py-1 font-mono text-xs uppercase tracking-widest transition-colors ${
                  filter === t
                    ? "border-brand bg-brand text-black"
                    : "border-neutral-700 text-neutral-300 hover:border-brand hover:text-brand"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div
          data-testid="traening-table"
          className="mt-12 overflow-hidden border border-neutral-800"
        >
          <div className="hidden grid-cols-[120px_1fr_140px_140px_1fr] gap-0 border-b border-neutral-800 bg-black md:grid">
            {["Dag", "Hold", "Tid", "Bane", "Træner"].map((h) => (
              <div key={h} className="mono-label px-5 py-4 text-neutral-500">
                {h}
              </div>
            ))}
          </div>

          {loading && (
            <div className="p-6 text-center text-sm text-neutral-500">Loader…</div>
          )}

          <div className="divide-y divide-neutral-900">
            {rows.map((r, i) => (
              <div
                key={r.id || `${r.team}-${r.day}-${r.time}-${i}`}
                data-testid={`traening-row-${i}`}
                className="group grid grid-cols-2 gap-2 bg-black/40 px-5 py-4 transition-colors hover:border-l-4 hover:border-brand hover:bg-black/70 md:grid-cols-[120px_1fr_140px_140px_1fr] md:gap-0 md:px-0"
              >
                <div className="mono-label text-brand md:px-5 md:py-1">
                  {r.day}
                </div>
                <div className="font-heading text-lg uppercase tracking-tight text-white md:px-5 md:py-1 md:text-xl">
                  {r.team}
                </div>
                <div className="col-span-2 font-mono text-sm text-neutral-200 md:col-span-1 md:px-5 md:py-1">
                  {r.time}
                </div>
                <div className="font-mono text-sm text-neutral-400 md:px-5 md:py-1">
                  {r.pitch}
                </div>
                <div className="font-body text-sm text-neutral-300 md:px-5 md:py-1">
                  {r.coach}
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="mono-label mt-6 text-neutral-500" data-testid="traening-note">
          * Officielle planer udgives kvartalsvist af Turneringsudvalget. Senest opdateret i admin-panelet.
        </p>
      </div>
    </section>
  );
};
